import dayjs, { Dayjs } from "dayjs";
import {
  DEFAULT_ANSWERS_TIME_MINUTES,
  getPreset,
  matchPresetId,
  type ContestPresetId,
} from "./contest-presets";
import {
  DEFAULT_TIMEZONE_ID,
  utcIsoToWallTime,
  wallTimeToUtcIso,
  type ContestTimezoneId,
} from "./contest-timezone";

export type ContestSendEmailChoice = boolean | null;

export interface ContestFormSettings {
  contestId: string;
  title: string;
  answersTimeMinutes: string;
  activeTimeMinutes: string;
  startTime: Dayjs | null;
  timezoneId: ContestTimezoneId;
  presetId: ContestPresetId;
  sendEmail: ContestSendEmailChoice;
}

export interface HydratedContestSettings {
  contestId: string;
  title: string;
  answersTimeMinutes: string;
  activeTimeMinutes: string;
  startTime: Dayjs;
  timezoneId: ContestTimezoneId;
  presetId: ContestPresetId;
  sendEmail: ContestSendEmailChoice;
}

export const formatJson = (value: string): string => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

export const tryParseContestJson = (
  value: string
): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
};

const normalizeTimeToSend = (value: string): string => {
  // Tolerate invalid "HH:mm:ss:ssZ" from some source files.
  return value.replace(
    /T(\d{2}):(\d{2}):(\d{2}):\d{2}Z$/i,
    "T$1:$2:$3Z"
  );
};

const parseTimeToSend = (
  value: unknown,
  timezoneId: ContestTimezoneId
): Dayjs | null => {
  if (typeof value !== "string" || !value.trim()) return null;
  return utcIsoToWallTime(normalizeTimeToSend(value.trim()), timezoneId);
};

const secondsToMinutes = (seconds: unknown, fallback: number): number => {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) {
    return fallback;
  }
  return Math.max(1, Math.round(seconds / 60));
};

const readContestId = (assessment: Record<string, unknown>): string => {
  if (typeof assessment.id === "string") return assessment.id;
  if (typeof assessment.assessment_id === "string") {
    return assessment.assessment_id;
  }
  return "";
};

const readContestTitle = (assessment: Record<string, unknown>): string =>
  typeof assessment.title === "string" ? assessment.title : "";

export const defaultContestStartTime = (): Dayjs =>
  dayjs().startOf("minute");

export const hydrateContestSettings = (
  assessment: Record<string, unknown>,
  timezoneId: ContestTimezoneId = DEFAULT_TIMEZONE_ID
): HydratedContestSettings => {
  const answersTimeMinutes = secondsToMinutes(
    assessment.answers_time_limit ?? assessment.active_time,
    DEFAULT_ANSWERS_TIME_MINUTES
  );

  return {
    contestId: readContestId(assessment),
    title: readContestTitle(assessment),
    answersTimeMinutes: String(answersTimeMinutes),
    activeTimeMinutes: String(
      secondsToMinutes(
        assessment.active_time ?? assessment.answers_time_limit,
        answersTimeMinutes
      )
    ),
    startTime:
      parseTimeToSend(assessment.time_to_send, timezoneId) ??
      defaultContestStartTime(),
    timezoneId,
    presetId: matchPresetId(assessment),
    sendEmail: null,
  };
};

export const applyContestSettingsToJson = (
  assessment: Record<string, unknown>,
  settings: ContestFormSettings
): Record<string, unknown> | null => {
  const answersMinutes = Number(settings.answersTimeMinutes);
  const activeMinutes = Number(settings.activeTimeMinutes);
  if (!Number.isInteger(answersMinutes) || answersMinutes <= 0) return null;
  if (!Number.isInteger(activeMinutes) || activeMinutes <= 0) return null;
  if (!settings.startTime || !settings.startTime.isValid()) return null;

  const contestId = settings.contestId.trim();
  if (!contestId) return null;

  const preset = getPreset(settings.presetId);

  const updated: Record<string, unknown> = {
    ...assessment,
    id: contestId,
    title: settings.title.trim(),
    time_to_send: wallTimeToUtcIso(settings.startTime, settings.timezoneId),
    answers_time_limit: answersMinutes * 60,
    active_time: activeMinutes * 60,
    contest: {
      ...(typeof assessment.contest === "object" && assessment.contest
        ? (assessment.contest as Record<string, unknown>)
        : {}),
      ...preset.contest,
    },
    gamification: {
      ...(typeof assessment.gamification === "object" && assessment.gamification
        ? (assessment.gamification as Record<string, unknown>)
        : {}),
      ...preset.gamification,
    },
  };

  // Only set email_sent once the required Yes/No is chosen.
  if (settings.sendEmail !== null) {
    updated.email_sent = settings.sendEmail === false;
  }

  return updated;
};
